import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Mail, User, CheckCircle } from "lucide-react";

interface NewsletterModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const NewsletterModal = ({ isOpen, onClose }: NewsletterModalProps) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  const resetForm = () => {
    setEmail('');
    setName('');
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const checkRateLimit = async () => {
    try {
      const response = await supabase.functions.invoke('rate-limiter', {
        body: { 
          action: 'newsletter_subscription',
          identifier: 'ip_' + (new Date().getTime().toString().slice(-6)) // Simple client fingerprint
        }
      });

      if (response.error) {
        console.error('Rate limit check failed:', response.error);
        return true; // Allow if rate limit service fails
      }

      return response.data?.allowed !== false;
    } catch (error) {
      console.error('Rate limit check error:', error);
      return true; // Allow if rate limit service fails
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email.trim()) {
      setError('Email is required');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Check rate limiting
    const rateLimitAllowed = await checkRateLimit();
    if (!rateLimitAllowed) {
      setError('Too many subscription attempts. Please wait an hour before trying again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data, error: insertError } = await supabase
        .from('subscribers')
        .insert([{
          email: email.trim().toLowerCase(),
          name: name.trim() || null
        }])
        .select()
        .single();

      if (insertError) {
        // Check if it's a duplicate email error
        if (insertError.code === '23505' && insertError.message.includes('unique')) {
          setError('This email is already subscribed to our newsletter.');
        } else {
          console.error('Subscription error:', insertError);
          setError('Failed to subscribe. Please try again.');
        }
        return;
      }

      setSuccess(true);
      toast({
        title: "Success!",
        description: "Thank you for subscribing to our newsletter!",
      });

      // Auto-close after success
      setTimeout(() => {
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('Error subscribing:', error);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="h-5 w-5" />
            Subscribe to Soundfield
          </DialogTitle>
        </DialogHeader>
        
        {success ? (
          <div className="space-y-4 py-4">
            <div className="text-center">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              <h3 className="text-lg font-semibold mb-2">Thank you for subscribing!</h3>
              <p className="text-muted-foreground">
                You'll receive the latest music reviews, artist features, and industry insights directly in your inbox.
              </p>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="newsletter-name">Name (optional)</Label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="newsletter-name"
                  type="text"
                  placeholder="Your name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10"
                  disabled={loading}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="newsletter-email">Email *</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                <Input
                  id="newsletter-email"
                  type="email"
                  placeholder="your@email.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="pl-10"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="flex gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                className="flex-1"
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="flex-1"
                disabled={loading}
              >
                {loading ? 'Subscribing...' : 'Subscribe'}
              </Button>
            </div>

            <p className="text-xs text-muted-foreground text-center">
              By subscribing, you agree to receive email updates from Soundfield. You can unsubscribe at any time.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default NewsletterModal;